import { createApi } from "@reduxjs/toolkit/query/react";
import { customBaseQuery } from "@/config/customBaseQuery";
import { getErrorPayload } from "@/utils/reduxError";
import { APICall } from "@/config/NetworkUtility";
import { baseURL } from "@/config/APIConfig";

const INTTRA_BASE_URL = baseURL.AiBaseURL || "";

/** Official INTTRA Ocean Execution booking paths (proxied by our backend). */
const getInttraBookingUrl = (
    bookingState = "REQUEST",
    inttraReferenceId = null,
    companyId = null
) => {
    const state = String(bookingState || "REQUEST").toUpperCase();
    let path;
    if (state === "AMEND") {
        path = `/api/inttra/booking/${encodeURIComponent(inttraReferenceId)}/amend`;
    } else if (state === "CANCEL") {
        path = `/api/inttra/booking/${encodeURIComponent(inttraReferenceId)}/cancel`;
    } else {
        path = "/api/inttra/booking/request";
    }
    const cid = Number(companyId);
    const qs = cid > 0 ? `?company_id=${encodeURIComponent(cid)}` : "";
    return `${INTTRA_BASE_URL}${path}${qs}`;
};

const inttraBaseQuery = async ({ url, method = "POST", body = null }) => {
    try {
        // INTTRA calls can exceed the default 15s API timeout
        const result = await APICall(method, body, url, { timeout: 60000 });

        if (
            result?.disposition === "APPLIED" &&
            (!result?.errors || result.errors.length === 0)
        ) {
            return { data: result };
        }

        if (result?.errors?.length > 0) {
            return {
                error: {
                    status: 400,
                    data: {
                        message: "INTTRA booking failed.",
                        detail: JSON.stringify(result.errors),
                        errors: result.errors,
                        response: result,
                    },
                },
            };
        }

        return {
            error: {
                status: 400,
                data: {
                    message: "Unexpected INTTRA response.",
                    detail: result?.disposition
                        ? `INTTRA disposition: ${result.disposition}`
                        : "Unexpected INTTRA response.",
                    response: result,
                },
            },
        };
    } catch (error) {
        return { error: getErrorPayload(error) };
    }
};

const asText = (value) => {
    if (value == null) return "";
    return String(value).trim();
};

/**
 * Prefer non-empty INTTRA response value; otherwise keep existing inbound value.
 * Prevents amend/cancel from wiping INTRA_* when response omits a field.
 */
const preferResponse = (responseValue, existingValue) =>
    asText(responseValue) || asText(existingValue);

/** CreateInbound / UpdateInbound — exact API keys only */
const toInboundPayload = (bookingData = {}, overrides = {}) => ({
    BKG_ID: Number(bookingData?.BKG_ID) || 0,
    BookingReferenceNumber: asText(bookingData?.BookingReferenceNumber),
    BOOKING_NUMBER: asText(bookingData?.BOOKING_NUMBER),
    CARRIER: asText(bookingData?.CARRIER),
    CARRIER_SCAC: asText(bookingData?.CARRIER_SCAC),
    INTRA_BOOKING_STATE: asText(bookingData?.INTRA_BOOKING_STATE),
    INTRA_BOOKING_ID: asText(bookingData?.INTRA_BOOKING_ID),
    INTRA_SEQUENCE_NUM: asText(bookingData?.INTRA_SEQUENCE_NUM),
    INTRA_REFERENCE_NUM: asText(bookingData?.INTRA_REFERENCE_NUM),
    REQUEST: bookingData?.REQUEST || "",
    RESPONSE: bookingData?.RESPONSE || "",
    COMPANY_ID: Number(bookingData?.COMPANY_ID) || 0,
    CREATED_BY: asText(bookingData?.CREATED_BY),
    ...overrides,
});

/**
 * INTTRA success response → CreateInbound INTRA_* fields.
 *
 * Response shape:
 *   disposition
 *   bookingDetails[0].bookingId
 *   bookingDetails[0].sequenceNumber
 *   bookingDetails[0].inttraReferenceNumber
 */
const getBookingDetail = (inttraData = {}) => {
    const details = inttraData?.bookingDetails;
    if (Array.isArray(details) && details.length > 0) return details[0];
    return null;
};

const buildInboundBody = (payload, bookingData, inttraData) => {
    const detail = getBookingDetail(inttraData);

    return toInboundPayload(bookingData, {
        INTRA_BOOKING_STATE: preferResponse(
            inttraData?.disposition,
            bookingData?.INTRA_BOOKING_STATE
        ),
        INTRA_BOOKING_ID: preferResponse(
            detail?.bookingId,
            bookingData?.INTRA_BOOKING_ID
        ),
        INTRA_SEQUENCE_NUM: preferResponse(
            detail?.sequenceNumber,
            bookingData?.INTRA_SEQUENCE_NUM
        ),
        INTRA_REFERENCE_NUM: preferResponse(
            detail?.inttraReferenceNumber,
            bookingData?.INTRA_REFERENCE_NUM
        ),
        REQUEST: JSON.stringify(payload ?? {}),
        RESPONSE: JSON.stringify(inttraData ?? {}),
    });
};

/**
 * Shared flow: INTTRA (request|amend|cancel) → save inbound on success only.
 * REQUEST → CreateInbound (once)
 * AMEND / CANCEL → UpdateInbound (once)
 */
const runInttraBookingFlow = async ({
    bookingState,
    payload,
    bookingData,
    inttraReferenceId = null,
    baseQuery,
}) => {
    const state = String(bookingState || "REQUEST").toUpperCase();

    if ((state === "AMEND" || state === "CANCEL") && !inttraReferenceId) {
        return {
            error: {
                status: 400,
                data: {
                    message: `${state} requires INTRA_REFERENCE_NUM from InboundList.`,
                },
            },
        };
    }

    const inttraResponse = await inttraBaseQuery({
        url: getInttraBookingUrl(state, inttraReferenceId, bookingData?.COMPANY_ID),
        method: "POST",
        body: payload,
    });

    if (inttraResponse.error) {
        return { error: inttraResponse.error };
    }

    const inttraData = inttraResponse.data;
    const inboundBody = buildInboundBody(payload, bookingData, inttraData);

    if (state === "REQUEST") {
        const createInboundResponse = await baseQuery({
            url: "/v2/Booking/CreateOutbound",
            method: "POST",
            body: inboundBody,
        });

        if (createInboundResponse.error) {
            return { error: createInboundResponse.error };
        }

        return {
            data: {
                bookingState: state,
                inttraReferenceId: inboundBody.INTRA_REFERENCE_NUM || null,
                inttraBooking: inttraData,
                createInbound: createInboundResponse.data,
            },
        };
    }

    const updateInboundResponse = await baseQuery({
        url: "/v2/Booking/CreateOutbound",
        method: "POST",
        body: inboundBody,
    });

    if (updateInboundResponse.error) {
        return { error: updateInboundResponse.error };
    }

    return {
        data: {
            bookingState: state,
            inttraReferenceId:
                inboundBody.INTRA_REFERENCE_NUM || inttraReferenceId,
            inttraBooking: inttraData,
            updateInbound: updateInboundResponse.data,
        },
    };
};

export const inttraApi = createApi({
    reducerPath: "inttraApi",
    baseQuery: customBaseQuery,
    endpoints: (builder) => ({
        /** POST /booking/request */
        requestBooking: builder.mutation({
            async queryFn(
                { payload, bookingData },
                _queryApi,
                _extraOptions,
                baseQuery
            ) {
                return runInttraBookingFlow({
                    bookingState: "REQUEST",
                    payload: { ...payload, bookingState: "REQUEST" },
                    bookingData,
                    baseQuery,
                });
            },
        }),

        /** POST /booking/{inttraReferenceId}/amend */
        amendBooking: builder.mutation({
            async queryFn(
                { payload, bookingData, inttraReferenceId },
                _queryApi,
                _extraOptions,
                baseQuery
            ) {
                return runInttraBookingFlow({
                    bookingState: "AMEND",
                    payload: { ...payload, bookingState: "AMEND" },
                    bookingData,
                    inttraReferenceId,
                    baseQuery,
                });
            },
        }),

        /** POST /booking/{inttraReferenceId}/cancel — body: { generalComments } */
        cancelBooking: builder.mutation({
            async queryFn(
                { payload, bookingData, inttraReferenceId },
                _queryApi,
                _extraOptions,
                baseQuery
            ) {
                return runInttraBookingFlow({
                    bookingState: "CANCEL",
                    payload: {
                        generalComments:
                            asText(payload?.generalComments) ||
                            "Booking cancellation requested",
                    },
                    bookingData,
                    inttraReferenceId,
                    baseQuery,
                });
            },
        }),
    }),
});

export const {
    useRequestBookingMutation,
    useAmendBookingMutation,
    useCancelBookingMutation,
} = inttraApi;
